import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Repeat2, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { labelOf, SelectField, useLookups } from "@/lib/crm";
import { StatusBadge, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TimelineList } from "@/components/crm/TimelineList";
import { LeadDialog, PRIORITIES } from "@/components/crm/LeadDialog";
import { LeadAppointmentDialog } from "@/components/crm/LeadAppointmentDialog";

export const MANUAL_PHASES = ["nuovo", "contattato", "appuntamento", "perso"];

const ConvertDialog = ({ open, onOpenChange, lead, onDone }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [f, setF] = useState({ company_id: "", company_name: "", vat_number: "", as_status: "prospect" });
  useEffect(() => { if (open) { setF({ company_id: lead.company_id || "", company_name: lead.company_name || "", vat_number: "", as_status: "prospect" }); setQ(lead.company_name || ""); } }, [open, lead]);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get("/crm/companies", { params: { q, limit: 5 } }).then((r) => setResults(r.data.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  const run = async () => {
    try {
      const body = f.company_id ? { company_id: f.company_id } : { company_name: f.company_name, vat_number: f.vat_number || null, as_status: f.as_status };
      const { data } = await api.post(`/crm/leads/${lead.id}/convert`, body);
      toast.success("Lead convertito"); onOpenChange(false); onDone(data.company_id);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="lead-convert-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Converti lead</DialogTitle><DialogDescription>Collega un'azienda esistente oppure creane una nuova. Il referente viene riutilizzato se già presente.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-2">
          <Label className="text-xs">Cerca azienda esistente</Label>
          <Input value={q} onChange={(e) => { setQ(e.target.value); setF({ ...f, company_id: "" }); }} data-testid="convert-search" />
          {results.length > 0 && <ul className="divide-y divide-line rounded-control border border-line">{results.map((r) => (
            <li key={r.id}><button onClick={() => setF({ ...f, company_id: r.id })} className={`w-full px-3 py-2 text-left text-sm ${f.company_id === r.id ? "bg-brand-50 font-semibold text-ink" : "hover:bg-slate-50"}`} data-testid={`convert-pick-${r.id}`}>{r.name}</button></li>))}</ul>}
          {!f.company_id && <div className="space-y-3 rounded-control bg-slate-50 p-3" data-testid="convert-new-company">
            <p className="text-xs font-semibold text-slate-600">Nuova azienda</p>
            <Input placeholder="Ragione sociale" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="convert-company-name" />
            <Input placeholder="Partita IVA (facoltativa)" value={f.vat_number} onChange={(e) => setF({ ...f, vat_number: e.target.value })} data-testid="convert-vat" />
            <SelectField value={f.as_status} onChange={(v) => setF({ ...f, as_status: v })} options={[{ key: "prospect", label: "Come prospect" }, { key: "attivo", label: "Come cliente attivo" }]} testId="convert-as-status" />
          </div>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={run} disabled={!f.company_id && f.company_name.trim().length < 2} data-testid="convert-confirm">Converti</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LeadSheet = ({ leadId, onClose, onChanged }) => {
  const { can } = useAuth();
  const lk = useLookups();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => { if (leadId) api.get(`/crm/leads/${leadId}`).then((r) => setLead(r.data)).catch((e) => toast.error(errorMessage(e))); }, [leadId]);
  useEffect(() => { setLead(null); load(); }, [load]);
  const refresh = () => { load(); onChanged(); };
  const setStatus = async (status) => {
    if (status === "appuntamento") { setModal("appointment"); return; }
    try { await api.patch(`/crm/leads/${leadId}`, { status }); toast.success("Fase aggiornata"); refresh(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const archive = async () => { try { await api.post(`/crm/leads/${leadId}/archive`); toast.success("Lead archiviato"); onChanged(); onClose(); } catch (e) { toast.error(errorMessage(e)); } };
  const converted = ["convertito", "qualificato"].includes(lead?.status);
  const editable = can("leads:update") && !converted;

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="lead-sheet">
        {lead && <>
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-xl" data-testid="lead-sheet-title">{lead.company_name || lead.contact_name}</SheetTitle>
            <SheetDescription>{[lead.contact_name, lead.email, lead.phone].filter(Boolean).join(" · ") || "—"}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <StatusBadge tone={labelOf(lk.lead_status, lead.status).tone} data-testid="lead-sheet-status">{labelOf(lk.lead_status, lead.status).label}</StatusBadge>
            <StatusBadge tone={labelOf(PRIORITIES, lead.priority).tone} dot={false}>Priorità {labelOf(PRIORITIES, lead.priority).label.toLowerCase()}</StatusBadge>
            <span className="text-xs text-slate-500">Responsabile: {lead.owner_name || "—"}</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {editable && <SelectField value={lead.status} onChange={setStatus} options={(lk.lead_status || []).filter((s) => MANUAL_PHASES.includes(s.key))} testId="lead-sheet-status-select" className="w-44" />}
            {editable && <Button variant="outline" size="sm" onClick={() => setModal("edit")} className="h-9 gap-1" data-testid="lead-sheet-edit"><Pencil />Modifica</Button>}
            {editable && <Button size="sm" onClick={() => setModal("convert")} className="h-9 gap-1" data-testid="lead-sheet-convert"><Repeat2 />Converti</Button>}
            {converted && lead.opportunity_id && <Button size="sm" variant="outline" onClick={() => nav(`/opportunita/${lead.opportunity_id}`)} data-testid="lead-sheet-open-opportunity">Apri opportunità</Button>}
            {converted && lead.company_id && <Button size="sm" onClick={() => nav(`/clienti/${lead.company_id}`)} data-testid="lead-sheet-open-company">Apri cliente</Button>}
            {can("leads:archive") && <Button variant="ghost" size="sm" onClick={archive} className="h-9 gap-1" data-testid="lead-sheet-archive"><Archive />Archivia</Button>}
          </div>
          {lead.notes && <p className="mt-5 whitespace-pre-line rounded-control bg-slate-50 p-3 text-sm text-slate-700">{lead.notes}</p>}
          <SectionLabel className="mb-3 mt-6">Timeline</SectionLabel>
          <TimelineList items={lead.timeline} />
          <LeadDialog open={modal === "edit"} onOpenChange={(o) => !o && setModal(null)} lead={lead} onSaved={refresh} />
          <ConvertDialog open={modal === "convert"} onOpenChange={(o) => !o && setModal(null)} lead={lead} onDone={(id) => { onChanged(); nav(`/clienti/${id}`); }} />
          <LeadAppointmentDialog open={modal === "appointment"} onOpenChange={(o) => !o && setModal(null)} lead={lead} onDone={() => refresh()} />
        </>}
      </SheetContent>
    </Sheet>
  );
};
